import React, { useEffect, useState } from 'react';
import { func } from 'prop-types';
import { connect } from 'react-redux';
import { Translate } from 'react-localize-redux';
import { Link, useParams } from 'react-router-dom';

import { getTranslation, withLang } from '../../i18n';
import { addErrorMessageAction, setTaskFulfilled, setTaskPending } from '../../actions';
import { getSurveyResponseForUser } from '../../api';
import { routes } from '../../constants';
import { langType, schemaPartItemTypeValues } from '../../constants/types';
import SchemaPartQuestionsPreview from '../Preview/SchemaPartPreview/QuestionsPreview';
import { autoFocusElementById, getCompiledPath } from '../../util';
import { usePreviousLocation } from '../PreviousLocationProvider';
import useTitle from '../../hooks/useTitle';

const questionResponseForQuestion = (question, questionResponses) => {
  const response = questionResponses.find((qr) => qr.questionId === question.id);
  let answer;
  if (response) {
    answer = response.questionResponse;
  }
  return {
    questionId: question.id, answer
  };
};

const questionResponseBySurveyPartItemForSurveyPart = (surveyPart, questionResponses) =>
  surveyPart.surveySchemaPartItems.reduce((acc, item) => {
    const {
      type, id, question, questionGroup
    } = item;
    if (![schemaPartItemTypeValues.QUESTION,
      schemaPartItemTypeValues.QUESTION_GROUP].includes(type)) {
      return acc;
    }

    let answers;
    if (schemaPartItemTypeValues.QUESTION === item.type) {
      answers = questionResponseForQuestion(question, questionResponses);
    }
    if (schemaPartItemTypeValues.QUESTION_GROUP === item.type) {
      answers = questionGroup.questions
        .map((q) => questionResponseForQuestion(q, questionResponses));
    }

    return { ...acc, [id]: answers };
  }, {});

const SURVEY_RESPONSE_TITLE = 'survey_response_title';

const ViewSurveyResponse = ({
  dispatchAddErrorMessage,
  dispatchSetTaskFulfilled,
  dispatchSetTaskPending,
  lang,
  translate
}) => {
  const [response, setResponse] = useState(null);
  const [entryReferer, setEntryReferer] = useState(null);
  const previousLocation = usePreviousLocation();
  const { surveyId } = useParams();

  useEffect(() => {
    autoFocusElementById(SURVEY_RESPONSE_TITLE);
    (async () => {
      dispatchSetTaskPending();
      try {
        const responseResult = await getSurveyResponseForUser(surveyId);
        setEntryReferer(previousLocation && previousLocation.pathname
          ? previousLocation.pathname
          : getCompiledPath(routes.survey.feedback.index, { surveyId }));
        setResponse(responseResult);
      } catch (e) {
        dispatchAddErrorMessage(e);
      } finally {
        dispatchSetTaskFulfilled();
      }
    })();
  }, []);

  const title = `${translate('viewSurveyResponse.title')} - ${response ? getTranslation(response.surveyTitle, lang) : ''}`;
  useTitle(title);

  if (!response) {
    return null;
  }

  const ReturnLink = () => (
    <Link to={entryReferer} className="theme-transparent button--action-before icon--arrow-left">
      <Translate id="common.back" />
    </Link>
  );

  return (
    <div className="page-container">
      <div className="flex-inline">
        <h1 className="page-title--small" id={SURVEY_RESPONSE_TITLE}>
          {title}
        </h1>
      </div>
      <ReturnLink />
      <div>
        {response.surveyParts.map((part, idx) => (
          <SchemaPartQuestionsPreview
            key={part.code}
            schemaPart={part}
            partNumber={idx + 1}
            answers={
              questionResponseBySurveyPartItemForSurveyPart(part, response.questionResponses)
}
            lang={lang}
            translate={translate}
          />
        ))}
        {response.researchConsentNeeded && (
          <div className="form__item">
            <input
              type="checkbox"
              id="researchConsentGiven"
              checked={response.researchConsentGiven}
              disabled
            />
            <label aria-label={translate('survey.researchConsent', lang)} htmlFor="researchConsentGiven"><Translate id="survey.researchConsent" /></label>
          </div>
        )}
      </div>
      <ReturnLink />
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  dispatchAddErrorMessage: (err) => dispatch(addErrorMessageAction(err)),
  dispatchSetTaskPending: () => dispatch(setTaskPending()),
  dispatchSetTaskFulfilled: () => dispatch(setTaskFulfilled())
});

ViewSurveyResponse.propTypes = {
  dispatchAddErrorMessage: func.isRequired,
  dispatchSetTaskPending: func.isRequired,
  dispatchSetTaskFulfilled: func.isRequired,
  ...langType
};

export default withLang(connect(null, mapDispatchToProps)(ViewSurveyResponse));
